import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@/shared/hooks/useNotification';
import { usePostApiAdminGuides } from '@/features/guides/api/generated';
import { useGuideForm } from './useGuideForm';
import { createGuidePayload, createGuideFormDataFromSearch } from '../utils/guideHelpers';
import { type VehicleSelectionValue } from '@/shared/components/VehicleEnrichment';

export interface GuideCreationState {
  isOpen: boolean;
  problem: string;
  vehicle: VehicleSelectionValue;
}

const initialState: GuideCreationState = {
  isOpen: false,
  problem: '',
  vehicle: {}
};

export interface UseGuideCreationOptions {
  /** Whether to navigate to the created guide after success */
  navigateOnSuccess?: boolean;
  /** Custom success callback */
  onSuccess?: (guideId?: string) => void;
  /** Custom error callback */
  onError?: (error: unknown) => void;
}

export const useGuideCreation = (options: UseGuideCreationOptions = {}) => {
  const { navigateOnSuccess = false, onSuccess, onError } = options;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { notification } = useNotification();
  
  const [creationState, setCreationState] = useState<GuideCreationState>(initialState);
  
  const {
    formState,
    isDirty,
    updateContentField,
    updateRootVehicleWithSelection,
    addSolution,
    updateSolution,
    removeSolution,
    reorderSolutions,
    addFilter,
    removeFilterAt,
    updateFilterAt,
    resetForm,
  } = useGuideForm();

  const { mutateAsync: createGuide, isPending: isCreating } = usePostApiAdminGuides();

  const openCreationDialog = useCallback((problem: string, vehicle: VehicleSelectionValue) => {
    const initialData = createGuideFormDataFromSearch(problem, vehicle);
    resetForm(initialData);
    setCreationState({
      isOpen: true,
      problem,
      vehicle
    });
  }, [resetForm]);

  const closeCreationDialog = useCallback(() => {
    setCreationState(initialState);
  }, []);

  const createNewGuide = useCallback(async () => {
    if (!formState.content.problem.trim()) {
      notification.error(t('guides.validation.problemRequired'));
      return;
    }

    const payload = createGuidePayload(formState);

    try {
      const response = await createGuide({ data: payload });
      notification.success(t('guides.createSuccess'));
      
      closeCreationDialog();
      
      if (onSuccess) {
        onSuccess(response.guide?.id || undefined);
      } else if (navigateOnSuccess) {
        if (response.guide?.id) {
          navigate(`/guides/${response.guide.id}`);
        } else {
          navigate('/guides');
        }
      }
    } catch (error) {
      notification.error(t('guides.createError'));
      if (onError) {
        onError(error);
      }
    }
  }, [
    formState.content.problem,
    formState,
    createGuide,
    notification,
    t,
    closeCreationDialog,
    onSuccess,
    onError,
    navigateOnSuccess,
    navigate
  ]);

  return {
    // State
    creationState,
    formState,
    isDirty,
    isCreating,

    // Actions
    openCreationDialog,
    closeCreationDialog,
    createNewGuide,

    // Form handlers
    updateContentField,
    updateRootVehicleWithSelection,
    addSolution,
    updateSolution,
    removeSolution,
    reorderSolutions,
    addFilter,
    removeFilterAt,
    updateFilterAt,
  };
};